import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { BLACK, PRIMARY_PURPLE } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import SectionCard from '../../../positionTemplate/components/SectionCard';
import { DEFAULT_NEW_SHIFT } from '../../constants';
import AutoSaveTextField from '../AutoSaveTextField';
import FieldLabel from '../FieldLabel';
import SaveStatusIndicator from '../SaveStatusIndicator';
import SectionTitle from '../SectionTitle';

import ShiftRow, { SHIFT_GRID_COLUMNS } from './ShiftRow';

const pillSx = {
  borderRadius: '12px',
  fontFamily: PRIMARY_FONT[500],
  fontSize: '11px',
  padding: '1px 8px',
  whiteSpace: 'nowrap',
};

const shiftCountPillSx = { ...pillSx, border: `1px solid ${BLACK[40]}`, color: BLACK[80] };

const plannedPillSx = { ...pillSx, backgroundColor: PRIMARY_PURPLE[20], color: PRIMARY_PURPLE[80] };

const titleRowSx = { alignItems: 'center', display: 'flex', gap: '12px' };

const addButtonSx = {
  borderColor: PRIMARY_PURPLE[70],
  borderRadius: '6px',
  color: PRIMARY_PURPLE[70],
  fontFamily: PRIMARY_FONT[500],
  textTransform: 'none',
};

const tableSx = { margin: '-8px -16px' };

const headerRowSx = {
  alignItems: 'center',
  borderBottom: `1px solid ${BLACK[20]}`,
  columnGap: '12px',
  display: 'grid',
  gridTemplateColumns: SHIFT_GRID_COLUMNS,
  padding: '10px 16px',
};

const payRateHeaderSx = { alignItems: 'center', display: 'flex', gap: '6px' };

const emptySx = { color: BLACK[70], fontSize: '14px', padding: '12px 16px' };

const footnoteSx = {
  alignItems: 'flex-start',
  backgroundColor: BLACK[10],
  color: BLACK[70],
  display: 'flex',
  gap: '8px',
  padding: '12px 16px',
};

const overstaffingRowSx = { borderBottom: `1px solid ${BLACK[20]}`, padding: '12px 16px' };

const footnoteIconSx = { fontSize: '16px' };

const footnoteTextSx = { fontSize: '12px' };

const toPercentInput = (decimal) =>
  typeof decimal === 'number' ? `${Math.round(decimal * 10000) / 100}` : '';

const isValidPercent = (value) => /^\d+(\.\d{1,2})?$/.test(value.trim()) && Number(value) <= 100;

const ShiftsCard = ({ positionTemplate, saveField, getStatus }) => {
  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const shifts = positionTemplate.shifts || [];
  const [draftShifts, setDraftShifts] = useState([]);
  const nextDraftId = useRef(0);

  const saveShifts = (nextShifts) =>
    saveField({
      key: 'shifts',
      fields: { shifts: nextShifts },
      patch: { shifts: nextShifts },
      fromResponse: (document) =>
        Array.isArray(document?.shifts) &&
        document.shifts.length === nextShifts.length && {
          shifts: document.shifts.map((saved, index) => ({ ...nextShifts[index], ...saved })),
        },
    });

  const addDraft = () => {
    nextDraftId.current += 1;
    setDraftShifts((prev) => [{ ...DEFAULT_NEW_SHIFT, draftId: nextDraftId.current }, ...prev]);
  };

  const removeDraft = (draftId) =>
    setDraftShifts((prev) => prev.filter((draft) => draft.draftId !== draftId));

  const headerLabels = [
    'pt.detail.shifts.column.name',
    'pt.detail.shifts.column.start',
    'pt.detail.shifts.column.end',
  ];

  return (
    <SectionCard
      header={
        <>
          <Box sx={titleRowSx}>
            <SectionTitle>{t('pt.detail.shifts.title')}</SectionTitle>
            <Box data-testid="pt-detail-shift-count" sx={shiftCountPillSx}>
              {t('pt.detail.summary.shiftCount', { count: shifts.length })}
            </Box>
            <SaveStatusIndicator status={getStatus('shifts')} />
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDraft} sx={addButtonSx}>
            {t('pt.detail.shifts.add')}
          </Button>
        </>
      }
    >
      <Box sx={tableSx}>
        <Box sx={headerRowSx}>
          {headerLabels.map((id) => (
            <FieldLabel key={id}>{t(id)}</FieldLabel>
          ))}
          <Box sx={payRateHeaderSx}>
            <FieldLabel>{t('pt.detail.shifts.column.payRate')}</FieldLabel>
            <Box sx={plannedPillSx}>{t('pt.detail.shifts.planned')}</Box>
          </Box>
          <span />
        </Box>

        {draftShifts.map((draft) => (
          <ShiftRow
            key={`draft-${draft.draftId}`}
            isNew
            shift={draft}
            basePayRate={positionTemplate.payRateReg}
            onSave={(shift) => {
              saveShifts([shift, ...shifts]);
              removeDraft(draft.draftId);
            }}
            onCancelNew={() => removeDraft(draft.draftId)}
          />
        ))}
        {shifts.map((shift, index) => (
          <ShiftRow
            // eslint-disable-next-line react/no-array-index-key
            key={shift._id || `shift-${index}`}
            shift={shift}
            basePayRate={positionTemplate.payRateReg}
            onSave={(next) =>
              saveShifts(shifts.map((s, i) => (i === index ? { ...s, ...next } : s)))
            }
            onRemove={() => saveShifts(shifts.filter((s, i) => i !== index))}
          />
        ))}
        {shifts.length === 0 && draftShifts.length === 0 && (
          <Typography sx={emptySx}>{t('pt.detail.shifts.empty')}</Typography>
        )}

        <Box sx={overstaffingRowSx}>
          <AutoSaveTextField
            id="pt-detail-overstaffingPercent"
            label={t('pt.detail.shifts.overstaffing')}
            value={toPercentInput(positionTemplate.overstaffingPercent)}
            status={getStatus('overstaffingPercent')}
            helperText={t('pt.detail.shifts.overstaffing.helper')}
            validate={(value) =>
              isValidPercent(value) ? null : t('pt.detail.shifts.overstaffing.invalid')
            }
            endAdornment="%"
            inputWidth="120px"
            onSave={(value) => {
              const overstaffingPercent = Number(value) / 100;
              saveField({
                key: 'overstaffingPercent',
                fields: { overstaffingPercent },
                patch: { overstaffingPercent },
              });
            }}
          />
        </Box>

        <Box sx={footnoteSx}>
          <InfoOutlinedIcon sx={footnoteIconSx} />
          <Typography sx={footnoteTextSx}>
            {t('pt.detail.shifts.footnote', {
              b: (chunks) => <strong key="b">{chunks}</strong>,
            })}
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
};

ShiftsCard.propTypes = {
  positionTemplate: PropTypes.shape({
    payRateReg: PropTypes.number,
    overstaffingPercent: PropTypes.number,
    shifts: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  saveField: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
};

export default ShiftsCard;
